/**
 * ClientEditorSheet — Sheet latéral pour créer/éditer un client.
 *
 * Utilisé depuis :
 *   - /app/clients (bouton "Nouveau client")
 *   - SalesDocumentEditor > ClientCombobox > "+ Créer nouveau"
 *
 * Prop `onSaved(client)` reçoit le client persisté (utile pour attacher
 * immédiatement au devis en cours de saisie).
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
SheetDescription, } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Save, User, Building2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", email: "", phone: "",
  address: "", postal_code: "", city: "", country: "France",
  siren: "", siret: "", vat_number: "",
  contact_name: "", contact_email: "",
  is_recurring: false, notes: "", tags: [],
};

export default function ClientEditorSheet({
  clientId, initial, open, onOpenChange, onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEdit = !!clientId;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({ ...EMPTY, ...initial });
      return;
    }
    if (clientId) {
      (async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/clients/${clientId}`);
          setForm({ ...EMPTY, ...data.client });
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
      })();
    } else {
      setForm(EMPTY);
    }
  }, [clientId, open, initial]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Le nom du client est obligatoire."); return; }
    setSaving(true);
    try {
      const clean = { ...form };
      Object.keys(clean).forEach((k) => {
        if (clean[k] === "") clean[k] = null;
      });
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/clients/${clientId}`, clean);
        saved = data.client;
        toast.success("Client mis à jour");
      } else {
        const { data } = await api.post("/clients", clean);
        saved = data.client;
        toast.success("Client créé");
      }
      onSaved?.(saved);
      onOpenChange?.(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-white p-0 flex flex-col"
                     data-testid="client-editor-sheet">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 shrink-0">
          <SheetTitle className="flex items-center gap-2 text-slate-900"
                       style={{ fontFamily: "Manrope, sans-serif" }}>
            {isEdit ? <User className="w-5 h-5 text-blue-600" /> : <Building2 className="w-5 h-5 text-emerald-600" />}
            {isEdit ? "Modifier le client" : "Nouveau client"}
          </SheetTitle><SheetDescription className="sr-only">Détails du panneau latéral.</SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-6 py-5 space-y-4">
            {loading ? <div className="skeleton h-40 rounded-lg" /> : (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Nom / Raison sociale *</Label>
                  <Input value={form.name} onChange={(e) => upd("name", e.target.value)}
                          className="mt-1 bg-white border-slate-200"
                          data-testid="client-form-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input type="email" value={form.email || ""}
                            onChange={(e) => upd("email", e.target.value)}
                            className="mt-1 bg-white border-slate-200"
                            data-testid="client-form-email" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Téléphone</Label>
                    <Input value={form.phone || ""}
                            onChange={(e) => upd("phone", e.target.value)}
                            className="mt-1 bg-white border-slate-200 font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Adresse</Label>
                  <Input value={form.address || ""}
                          onChange={(e) => upd("address", e.target.value)}
                          className="mt-1 bg-white border-slate-200"
                          data-testid="client-form-address" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">CP</Label>
                    <Input value={form.postal_code || ""}
                            onChange={(e) => upd("postal_code", e.target.value)}
                            className="mt-1 bg-white border-slate-200 font-mono"
                            data-testid="client-form-postal-code" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Ville</Label>
                    <Input value={form.city || ""}
                            onChange={(e) => upd("city", e.target.value)}
                            className="mt-1 bg-white border-slate-200"
                            data-testid="client-form-city" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">SIREN</Label>
                    <Input value={form.siren || ""}
                            onChange={(e) => upd("siren", e.target.value)}
                            className="mt-1 bg-white border-slate-200 font-mono"
                            data-testid="client-form-siren" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">SIRET</Label>
                    <Input value={form.siret || ""}
                            onChange={(e) => upd("siret", e.target.value)}
                            className="mt-1 bg-white border-slate-200 font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">TVA intracom.</Label>
                  <Input value={form.vat_number || ""}
                          onChange={(e) => upd("vat_number", e.target.value)}
                          className="mt-1 bg-white border-slate-200 font-mono" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Contact principal</Label>
                    <Input value={form.contact_name || ""}
                            onChange={(e) => upd("contact_name", e.target.value)}
                            className="mt-1 bg-white border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email contact</Label>
                    <Input type="email" value={form.contact_email || ""}
                            onChange={(e) => upd("contact_email", e.target.value)}
                            className="mt-1 bg-white border-slate-200" />
                  </div>
                </div>
                <div className="flex items-center gap-3 py-2 border-t border-slate-100">
                  <Switch checked={!!form.is_recurring}
                           onCheckedChange={(v) => upd("is_recurring", v)}
                           data-testid="client-form-recurring" />
                  <Label className="text-sm text-slate-700">Client récurrent</Label>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Notes internes</Label>
                  <Textarea value={form.notes || ""}
                             onChange={(e) => upd("notes", e.target.value)}
                             className="mt-1 bg-white border-slate-200 min-h-[80px]"
                             placeholder="Non visible par le client" />
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        <div className="border-t border-slate-200 px-6 py-3 flex justify-end gap-2 shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange?.(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving || loading}
                   className="bg-blue-600 hover:bg-blue-700 text-white"
                   data-testid="client-form-save-btn">
            <Save className="w-4 h-4 mr-1.5" />
            {isEdit ? "Enregistrer" : "Créer"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
